import { ApiResponse, PaginatedApiRequest, PaginatedApiResponse } from './ServiceModels';

/**
 * Banner types
 */
export enum BannerType {
  PLANNED_CHANGE = 'PLANNED_CHANGE',
  OUTAGE = 'OUTAGE',
  OTHER = 'OTHER',
}

/**
 * Timezone selected by default on the banner form
 */
export const defaultBannerTimezone = 'UTC';

/**
 * Value of the default option on the banner form timezone dropdowns
 */
export const defaultTimezoneOption = 'default-timezone';

/**
 * Message template translation key for each templated banner type
 */
export const bannerTemplateKeys: Partial<Record<BannerType, string>> = {
  [BannerType.PLANNED_CHANGE]: 'plannedChange',
  [BannerType.OUTAGE]: 'outage',
};

/**
 * Banner scopes
 */
export enum BannerScope {
  GLOBAL = 'GLOBAL',
  MARKETS = 'MARKETS',
  COMPANIES = 'COMPANIES',
}

/**
 * Banner type option
 */
export interface BannerTypeOption {
  value: BannerType;
  icon: JSX.Element;
  title: string;
  description: string;
}

/**
 * Banner scope option
 */
export interface BannerScopeOption {
  value: BannerScope;
  title: string;
  description: string;
}

/**
 * Lifecycle status of a created banner
 */
export enum BannerStatus {
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  ACTIVE = 'ACTIVE',
  CANCELED = 'CANCELED',
  EXPIRED = 'EXPIRED',
}

/**
 * Banner model
 */
export interface Banner {
  id: string;
  bannerType: BannerType | '';
  scope: BannerScope | '';
  selectedMarkets: string[];
  selectedCompanies: string[];
  bannerTitle: string;
  timezone: string;
  startDate: string;
  endDate: string;
  maintenanceTimezone: string;
  maintenanceStart: string;
  maintenanceEnd: string;
  messages: Record<string, string>;
  dismissible: boolean;
  status: BannerStatus;
  createdBy: string;
}

/**
 * Banner model for form data
 */
export type BannerFormData = Omit<Banner, 'id' | 'status' | 'createdBy'> & {
  timezoneOption: string;
  maintenanceTimezoneOption: string;
};

/**
 * Banner model as returned by the API
 */
export interface BannerResponse extends ApiResponse {
  id: string;
  bannerType: BannerType;
  scopeType: BannerScope;
  title: string;
  status: BannerStatus;
  startAt: string;
  endAt: string;
  translations?: Record<string, string>;
  marketIds?: string[];
  companyIds?: string[];
  createdBy?: string;
  updatedBy?: string;
  canceledBy?: string;
  dateCreated?: string;
  dateUpdated?: string;
  canceledAt?: string;
  dismissible: boolean;
  maintenanceWindow: BannerMaintenanceWindow | null;
}

/**
 * API response for GET /notifications/banner/active
 */
export interface ActiveBannersResponse extends ApiResponse {
  banners: BannerResponse[];
}

/**
 * Banners list request
 * Request params to get the list of banners
 */
export interface BannersListRequest extends PaginatedApiRequest {
  bannerType?: BannerType;
  scopeType?: BannerScope;
  status?: BannerStatus;
  title?: string;
  createdBy?: string;
  marketId?: string;
  companyId?: string;
  activeOnly?: boolean;
}

/**
 * Banner overlaps request
 * Period and audience of the banner being prepared, used to find SCHEDULED or ACTIVE banners overlapping it
 */
export interface BannerOverlapsRequest {
  scopeType: BannerScope;
  startAt?: string;
  endAt?: string;
  marketIds?: string[];
  companyIds?: string[];
}

/**
 * API response for POST /notifications/banner/overlaps
 */
export interface BannerOverlapsResponse extends ApiResponse {
  hasOverlaps: boolean;
  overlappedBanners: BannerResponse[];
}

/**
 * Paginated banners list
 * Response from API
 */
export interface BannersListResponse extends PaginatedApiResponse {
  banners: BannerResponse[];
}

/**
 * Banner request interface used to create and update a banner
 */
export interface BannerRequest {
  bannerType: BannerType;
  scopeType?: BannerScope;
  title: string;
  startAt?: string;
  endAt?: string;
  translations: Record<string, string>;
  marketIds: string[];
  companyIds: string[];
  dismissible: boolean;
  maintenanceWindow: BannerMaintenanceWindow | null;
  isDraft: boolean;
}

/**
 * Maintenance window sent for Planned change banners
 */
interface BannerMaintenanceWindow {
  startAt?: string;
  endAt?: string;
}
